import React, { useState, useEffect } from 'react';
import adminAPI from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, X } from 'lucide-react';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '', goal_type: '5k', experience_level: 'intermediate', total_weeks: 12,
    description: '', blocks: [
      { phase: 'base', name: 'Build Foundation', weeks: 4, focus: 'Aerobic base', weekly_km_range: '20-30' },
      { phase: 'build', name: 'Build Strength', weeks: 4, focus: 'Tempo + intervals', weekly_km_range: '30-40' },
      { phase: 'peak', name: 'Peak', weeks: 2, focus: 'Race-specific', weekly_km_range: '35-45' },
      { phase: 'taper', name: 'Taper', weeks: 2, focus: 'Reduce volume', weekly_km_range: '15-25' },
    ]
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Navn er påkrevd'); return; }
    setCreating(true);
    try {
      await adminAPI.createTemplate(form);
      toast.success('Mal opprettet');
      setShowCreate(false);
      setForm({ name: '', goal_type: '5k', experience_level: 'intermediate', total_weeks: 12, description: '', blocks: form.blocks });
      loadTemplates();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Slett denne malen?')) return;
    try {
      await adminAPI.deleteTemplate(id);
      toast.success('Mal slettet');
      loadTemplates();
    } catch (err) { toast.error(err.message); }
  };

  const updateBlock = (idx, field, val) => {
    const newBlocks = [...form.blocks];
    newBlocks[idx] = { ...newBlocks[idx], [field]: val };
    setForm(prev => ({ ...prev, blocks: newBlocks }));
  };

  const addBlock = () => {
    setForm(prev => ({ ...prev, blocks: [...prev.blocks, { phase: 'base', name: '', weeks: 2, focus: '', weekly_km_range: '' }] }));
  };

  const removeBlock = (idx) => {
    setForm(prev => ({ ...prev, blocks: prev.blocks.filter((_, i) => i !== idx) }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div>
          <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl text-white">Treningsmaler</h1>
          <p className="text-xs sm:text-sm text-white/40 mt-1">{templates.length} maler</p>
        </div>
        <button
          data-testid="admin-templates-create-button"
          onClick={() => setShowCreate(true)}
          className="h-11 px-5 rounded-[14px] bg-[#CCFF00] text-black font-semibold text-sm flex items-center gap-2 hover:bg-[#D7FF3A] active:scale-[0.99] transition-[background-color,transform] duration-180 shadow-[0_8px_24px_rgba(204,255,0,0.12)]"
        >
          <Plus className="w-4 h-4" /> Ny mal
        </button>
      </div>

      {/* Template list */}
      <div data-testid="admin-templates-list" className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20 rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5">
            <FileText className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-sm text-white/30">Ingen maler opprettet ennå</p>
          </div>
        ) : templates.map((t) => (
          <div key={t.id || t._id} className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5 hover:bg-white/[0.04] hover:border-white/10 transition-[background-color,border-color] duration-180">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-['Barlow_Condensed'] font-bold text-xl text-white">{t.name}</h3>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[#CCFF00]/10 text-[#CCFF00] font-medium">{t.goal_type?.replace(/_/g, ' ')}</span>
                  <span className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider bg-white/5 text-white/50 font-medium">{t.experience_level}</span>
                  <span className="text-xs text-white/30">{t.total_weeks} uker</span>
                </div>
                {t.description && <p className="text-sm text-white/40 mt-2">{t.description}</p>}
                {t.blocks && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {t.blocks.map((b, i) => (
                      <span key={i} className="text-[10px] px-2 py-1 rounded-lg bg-white/[0.03] border border-white/[0.05] text-white/40">
                        {b.phase} · {b.weeks}u · {b.weekly_km_range}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => handleDelete(t.id || t._id)} className="p-2 rounded-lg text-white/20 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-[background-color,color] duration-180">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowCreate(false)} />
          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-[16px] bg-[#0A0A0A] border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Barlow_Condensed'] font-bold text-2xl text-white">Ny treningsmal</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 rounded-lg text-white/30 hover:text-white hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">NAVN</label>
                  <input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="w-full h-11 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white px-4 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35" placeholder="Halvmaraton 16u - Intermediate" />
                </div>
                <div>
                  <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">MÅLTYPE</label>
                  <select value={form.goal_type} onChange={(e) => setForm(p => ({ ...p, goal_type: e.target.value }))} className="w-full h-11 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white px-4 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35">
                    <option value="5k">5K</option>
                    <option value="10k">10K</option>
                    <option value="half_marathon">Halvmaraton</option>
                    <option value="marathon">Maraton</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">NIVÅ</label>
                  <select value={form.experience_level} onChange={(e) => setForm(p => ({ ...p, experience_level: e.target.value }))} className="w-full h-11 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white px-4 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35">
                    <option value="beginner">Begynner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Avansert</option>
                  </select>
                </div>
                <div>
                  <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">TOTALT UKER</label>
                  <input type="number" value={form.total_weeks} onChange={(e) => setForm(p => ({ ...p, total_weeks: parseInt(e.target.value) || 0 }))} className="w-full h-11 rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white px-4 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35" />
                </div>
              </div>
              <div>
                <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30 mb-2 block">BESKRIVELSE</label>
                <textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} rows={2} className="w-full rounded-[14px] bg-[#121212] border border-white/10 text-sm text-white p-4 placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-[#CCFF00]/35 resize-y" placeholder="Solid oppbygging mot halvmaraton" />
              </div>

              {/* Blocks */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="uppercase tracking-[0.18em] text-[10px] font-medium text-white/30">BLOKKER</label>
                  <button type="button" onClick={addBlock} className="text-xs text-[#CCFF00] hover:underline">+ Legg til</button>
                </div>
                <div className="space-y-3">
                  {form.blocks.map((b, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/40 uppercase">Blokk {i + 1}</span>
                        {form.blocks.length > 1 && <button type="button" onClick={() => removeBlock(i)} className="text-[10px] text-[#FF3B30] hover:underline">Fjern</button>}
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        <select value={b.phase} onChange={(e) => updateBlock(i, 'phase', e.target.value)} className="h-9 rounded-lg bg-[#121212] border border-white/10 text-xs text-white px-2 focus:outline-none">
                          <option value="base">Base</option>
                          <option value="build">Build</option>
                          <option value="peak">Peak</option>
                          <option value="taper">Taper</option>
                        </select>
                        <input value={b.name} onChange={(e) => updateBlock(i, 'name', e.target.value)} placeholder="Navn" className="h-9 rounded-lg bg-[#121212] border border-white/10 text-xs text-white px-2 focus:outline-none" />
                        <input type="number" value={b.weeks} onChange={(e) => updateBlock(i, 'weeks', parseInt(e.target.value) || 0)} placeholder="Uker" className="h-9 rounded-lg bg-[#121212] border border-white/10 text-xs text-white px-2 focus:outline-none" />
                        <input value={b.focus} onChange={(e) => updateBlock(i, 'focus', e.target.value)} placeholder="Fokus" className="h-9 rounded-lg bg-[#121212] border border-white/10 text-xs text-white px-2 focus:outline-none" />
                        <input value={b.weekly_km_range} onChange={(e) => updateBlock(i, 'weekly_km_range', e.target.value)} placeholder="km" className="h-9 rounded-lg bg-[#121212] border border-white/10 text-xs text-white px-2 focus:outline-none" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button type="submit" disabled={creating} className="w-full h-[52px] rounded-[14px] bg-[#CCFF00] text-black font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#D7FF3A] active:scale-[0.99] transition-[background-color,transform] duration-180 disabled:opacity-50">
                {creating ? <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : null}
                Opprett mal
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
